import { ModelMetadata, LoadStrategy } from './types';
import { BaseModel } from './BaseModel';

/**
 * ModelRegistry manages metadata for all models in the system.
 * Maps model names to constructors and property metadata.
 */
export class ModelRegistry {
  private static models: Map<string, ModelMetadata> = new Map();

  private static normalizeDateValue(value: unknown): unknown {
    if (value == null || value instanceof Date) {
      return value;
    }
    const parsed = new Date(value as string | number);
    return Number.isNaN(parsed.getTime()) ? value : parsed;
  }

  /**
   * Register a model with its metadata.
   */
  static registerModel(meta: ModelMetadata): void {
    this.models.set(meta.name, meta);
  }

  /**
   * Get model metadata by name.
   */
  static getModel(name: string): ModelMetadata | undefined {
    return this.models.get(name);
  }

  /**
   * Get all registered models.
   */
  static getAllModels(): ModelMetadata[] {
    return Array.from(this.models.values());
  }

  /**
   * Get models by load strategy.
   */
  static getModelsByLoadStrategy(strategy: LoadStrategy): ModelMetadata[] {
    return Array.from(this.models.values()).filter(
      (model) => model.loadStrategy === strategy
    );
  }

  /**
   * Check if a model is registered.
   */
  static hasModel(name: string): boolean {
    return this.models.has(name);
  }

  /**
   * Create an instance of a model from data.
   */
  static createModel<T extends BaseModel>(
    modelName: string,
    data: any
  ): T | null {
    const meta = this.getModel(modelName);
    if (!meta) {
      console.error(`Model ${modelName} not found in registry`);
      return null;
    }

    const instance = new meta.ctor() as T;
    Object.assign(instance, this.normalizeModelData(modelName, data));
    return instance;
  }

  /**
   * Normalize incoming model payloads using registered property metadata.
   */
  static normalizeModelData(modelName: string, data: any): any {
    const meta = this.getModel(modelName);
    if (!meta || !data || typeof data !== 'object' || Array.isArray(data)) {
      return data;
    }

    const normalized = { ...data };
    for (const [field, propertyMeta] of Object.entries(meta.properties)) {
      if (!(field in normalized)) {
        continue;
      }
      if (propertyMeta.type === 'date') {
        normalized[field] = this.normalizeDateValue(normalized[field]);
      }
    }

    return normalized;
  }
}

